import React, { useState, useEffect, useCallback } from 'react';
import { businessDaysBetween } from './executive/execUtils';
import { getSalesDay } from '../api';

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const getColor = (rate, ref) => {
  if (rate >= ref) return '#10b981';
  if (rate >= ref * 0.85) return '#facc15';
  return '#ef4444';
};

const fmtDate = (d) => d.toISOString().split('T')[0];

const fmtDateBR = (str) => {
  if (!str) return '';
  const [y, m, d] = str.split('-');
  return d + '/' + m + '/' + y;
};

const METRICAS_ORDER = [
  { keys: ['salesDay', 'fgPre'], titulo: 'FG Pré Aprovada' },
  { keys: ['mx'], titulo: 'Aprovações MX' },
  { keys: ['lm', 'lmBalcao'], titulo: 'LM Balcão' },
  { keys: ['lm_ih', 'lmInhome'], titulo: 'LM In-Home' },
  { keys: ['lb', 'linhaBranca'], titulo: 'Linha Branca' },
];

function SalesDayDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Default: 1o do mes atual ate ontem
  const hoje = new Date();
  const ontem = new Date(hoje);
  ontem.setDate(ontem.getDate() - 1);
  const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

  const [dataInicio, setDataInicio] = useState(fmtDate(inicioMes));
  const [dataFim, setDataFim] = useState(fmtDate(ontem));
  const [filtroExpandido, setFiltroExpandido] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getSalesDay(dataInicio, dataFim);
      setData(res.data);
    } catch (err) { setError(err.message); }
    setLoading(false);
  }, [dataInicio, dataFim]);

  useEffect(() => { loadData(); }, [loadData]);

  // Atalhos rapidos
  const setMes = (offset) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() + offset, 1);
    const fim = offset === 0 ? ontem : new Date(d.getFullYear(), d.getMonth() + 1, 0);
    setDataInicio(fmtDate(d));
    setDataFim(fmtDate(fim));
  };

  const metricasComDados = [];
  if (data?.empresas) {
    for (const metrica of METRICAS_ORDER) {
      const empresas = [];
      for (const [slug, empresa] of Object.entries(data.empresas)) {
        if (empresa.semDados) continue;
        // Ocultar FIX MSC da secao LM Balcao
        if (slug === 'fix-msc' && metrica.titulo === 'LM Balcão') continue;
        for (const key of metrica.keys) {
          const aba = empresa.abas[key];
          if (aba) { empresas.push({ slug, nome: empresa.nome, ...aba }); break; }
        }
      }
      if (empresas.length > 0) metricasComDados.push({ titulo: metrica.titulo, empresas });
    }
  }

  // Calcular esperado baseado no periodo
  const dtInicio = new Date(dataInicio + 'T00:00:00');
  const dtFim = new Date(dataFim + 'T00:00:00');
  const diasNoMes = new Date(dtInicio.getFullYear(), dtInicio.getMonth() + 1, 0).getDate();
  const diasNoPeriodo = Math.round((dtFim - dtInicio) / (1000 * 60 * 60 * 24)) + 1;

  // Esperado por DIAS ÚTEIS (seg–sex, sem feriados nacionais) — padronizado 21/09.
  const _mesIni = dataInicio ? dataInicio.slice(0, 8) + '01' : null;
  const _mesFim = (() => {
    if (!dataInicio) return null;
    const d = new Date(dataInicio + 'T00:00:00');
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
  })();
  // slug desconta também os feriados LOCAIS da cidade da loja (FERIADOS_LOCAIS em execUtils).
  const getEsperado = (metaMensal, slug) => {
    const uteisMes = (_mesIni && _mesFim) ? businessDaysBetween(_mesIni, _mesFim, slug) : 0;
    const uteisPeriodo = (dataInicio && dataFim) ? businessDaysBetween(dataInicio, dataFim, slug) : 0;
    const fator = uteisMes > 0 ? Math.min(uteisPeriodo / uteisMes, 1) : (diasNoPeriodo / diasNoMes);
    return Math.round(metaMensal * fator * 100) / 100;
  };

  const inputStyle = {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
    background: '#f8fafc', color: '#0f172a', fontSize: 13, fontFamily: 'inherit',
    outline: 'none',
  };

  const btnStyle = (active) => ({
    padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 600,
    background: active ? '#3b82f6' : 'rgba(15,23,42,0.08)',
    color: active ? '#fff' : '#475569',
    transition: 'all 0.2s',
  });

  // Detectar qual atalho ta ativo
  const mesAtualAtivo = dataInicio === fmtDate(inicioMes) && dataFim === fmtDate(ontem);
  const mesPassadoInicio = fmtDate(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));
  const mesPassadoFim = fmtDate(new Date(hoje.getFullYear(), hoje.getMonth(), 0));
  const mesPassadoAtivo = dataInicio === mesPassadoInicio && dataFim === mesPassadoFim;

  const MESES_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px', fontFamily: "'Samsung Sharp Sans', 'SamsungOne', 'Inter', -apple-system, sans-serif" }}>

      {/* Barra de filtro */}
      <div style={{ background: 'rgba(15,23,42,0.035)', borderRadius: 12, padding: '12px 16px', marginBottom: 20, border: '1px solid #cbd5e1' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>Período:</span>
          <button onClick={() => setMes(0)} style={btnStyle(mesAtualAtivo)}>Mês Atual</button>
          <button onClick={() => setMes(-1)} style={btnStyle(mesPassadoAtivo)}>Mês Anterior</button>
          <button onClick={() => setMes(-2)} style={btnStyle(!mesAtualAtivo && !mesPassadoAtivo && !filtroExpandido)}>2 meses atrás</button>
          <button onClick={() => setFiltroExpandido(!filtroExpandido)}
            style={{ ...btnStyle(filtroExpandido), marginLeft: 'auto' }}>
            {filtroExpandido ? 'Fechar' : 'Filtro livre'}
          </button>
        </div>

        {filtroExpandido && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: '#475569' }}>De:</label>
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} style={inputStyle} />
            <label style={{ fontSize: 12, color: '#475569' }}>Até:</label>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={inputStyle} />
          </div>
        )}

        <p style={{ fontSize: 11, color: '#475569', marginTop: 8 }}>
          {fmtDateBR(dataInicio)} a {fmtDateBR(dataFim)} ({diasNoPeriodo} dias)
        </p>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#475569' }}>Carregando...</div>}
      {error && <div style={{ padding: 20, color: '#b91c1c', background: 'rgba(239,68,68,0.1)', borderRadius: 8, textAlign: 'center' }}>{error}</div>}

      {!loading && metricasComDados.length === 0 && !error && (
        <div style={{ textAlign: 'center', padding: 40, color: '#475569' }}>Nenhum dado para o período selecionado.</div>
      )}

      {metricasComDados.map((metrica, idx) => (
        <div key={idx} style={{
          background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: 16, padding: '24px 20px', border: '1px solid #cbd5e1',
          boxShadow: '0 4px 20px rgba(15,23,42,0.10)', marginBottom: 24,
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 20, letterSpacing: '-0.02em' }}>
            {metrica.titulo}
          </h2>

          <p style={{ fontSize: 11, color: '#475569', marginBottom: 8, textAlign: 'right' }}>
            Arraste para ver mais &rarr;
          </p>
          <div style={{ display: 'flex', gap: 16, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 8, scrollbarWidth: 'none' }}>
            {metrica.empresas.map(e => {
              const espReceita = getEsperado(e.metaReceita, e.slug);
              const corAprov = getColor(e.approvalRate, e.metaAprovacao);
              const corReceita = e.totalRevenue >= espReceita ? '#10b981' : e.totalRevenue >= espReceita * 0.85 ? '#facc15' : '#ef4444';

              return (
                <div key={e.slug} style={{
                  minWidth: 260, flex: '0 0 auto',
                  background: 'rgba(15,23,42,0.035)', borderRadius: 14,
                  border: '1px solid rgba(15,23,42,0.08)',
                  borderLeft: `4px solid ${corAprov}`,
                  overflow: 'hidden',
                }}>
                  <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', letterSpacing: '0.02em' }}>{e.nome}</p>
                    <p style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{e.approvedOS} / {e.totalOS} OS aprovadas</p>
                  </div>

                  <div style={{ padding: '16px 16px 12px' }}>
                    <p style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                      Taxa de Aprovação
                    </p>
                    <p style={{ fontSize: 32, fontWeight: 900, color: corAprov, letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 12 }}>
                      {e.approvalRate.toFixed(1)}%
                    </p>
                    <div style={{ position: 'relative', height: 10, background: 'rgba(15,23,42,0.07)', borderRadius: 5, marginBottom: 14 }}>
                      <div style={{ height: '100%', borderRadius: 5, width: `${Math.min(e.approvalRate, 100)}%`, backgroundColor: corAprov, transition: 'width 0.8s' }} />
                      <div style={{
                        position: 'absolute', top: -3, left: `${Math.min(e.metaAprovacao, 100)}%`,
                        width: 3, height: 16, background: '#0f172a', borderRadius: 2, opacity: 0.6,
                      }} title={`Meta: ${e.metaAprovacao}%`} />
                    </div>
                    <div style={{ background: 'rgba(15,23,42,0.035)', borderRadius: 8, padding: '8px 10px', border: '1px solid rgba(15,23,42,0.08)' }}>
                      <p style={{ fontSize: 10, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Meta Tx. Aprovação</p>
                      <p style={{ fontSize: 18, fontWeight: 800, color: '#475569', marginTop: 2 }}>{e.metaAprovacao}%</p>
                    </div>
                  </div>

                  <div style={{ padding: '12px 16px 16px', background: 'rgba(15,23,42,0.05)', borderTop: '1px solid rgba(15,23,42,0.06)' }}>
                    <p style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                      Receita
                    </p>
                    <p style={{ fontSize: 20, fontWeight: 800, color: corReceita, marginBottom: 10 }}>
                      {formatCurrency(e.totalRevenue)}
                    </p>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 10, color: '#b45309', fontWeight: 600 }}>Esperado</p>
                        <p style={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>{formatCurrency(espReceita)}</p>
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 10, color: '#475569', fontWeight: 600 }}>Meta Faturamento</p>
                        <p style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>{formatCurrency(e.metaReceita)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default SalesDayDashboard;
